import React, { useCallback, useEffect, useState } from "react";
import { ROUTES, RACE_TYPES } from "../../../../consts";
import { useTrackContext } from "../../../../hooks/useTrackContext";
import { TRaceTypeFilter } from "../../../../types";
import ProgressBar from "../../../ProgressiveBar";
import { useNavigate } from "react-router-dom";
import ExportInfomationPopup from "./ExpertInformationPopup";
import MiniCart from "../../../Cart/MiniCart";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../../../config";
import { GET_METAOBJECT_BY_HANDLE } from "../../../../graphqlQueries";
import { MainTrackExpertVideo, MainTrackStandardVideo } from "../../../../consts/assetUrls";
import BottomSummary from "../../../BottomSummary";

const SelectRaceTypes: React.FC = () => {
  const { dispatch, mainTrackSelection, productRenderedLocation } = useTrackContext();
  const { raceType } = mainTrackSelection;
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const navigator = useNavigate();

  const dispatchRaceTypeUpdate = (updateRaceType: TRaceTypeFilter) => {
    dispatch({
      type: "SET_RACE_TYPE",
      payload: {
        selectedTrack: "MAIN_TRACK",
        raceType: updateRaceType,
      },
    });

    if (updateRaceType === raceType) {
      return navigator("/" + ROUTES.MAIN_TRACK.SEATS_RACES);
    }
  };

  const [isPopupShowing, setIsPopupShowing] = useState<boolean>(false);

  useEffect(() => {
    if (raceType === "EXPERT") {
      setIsPopupShowing(true);
    }
  }, [raceType]);

  const raceTypeStyleObject = useCallback(
    (raceType: TRaceTypeFilter) => {
      if (raceType === "EXPERT") {
        return {
          videoUrl: MainTrackExpertVideo,
          width: "100%",
          height: "100%",
          borderRadius: "22px",
        };
      } else if (raceType === "STANDARD") {
        return {
          videoUrl: MainTrackStandardVideo,
          width: "100%",
          height: "100%",
          borderRadius: "22px",
        };
      }
      return {
        width: "100%",
        height: "100%",
        background: "#393B41",
        borderRadius: "22px",
      };
    },
    [] // Update dependency array
  );

  const renderRaceTypeOption = (type: TRaceTypeFilter) => {
    const isActive = raceType === type;

    return (
      <div
        className={`${
          type === "STANDARD"
            ? "hover:tw-cursor-pointer tw-mx-auto lg:tw-mx-0 lg:tw-ml-auto"
            : type === "EXPERT"
            ? "hover:tw-cursor-pointer tw-mx-auto lg:tw-mx-0 lg:tw-mr-auto"
            : ""
        }`}
        key={type}
      >
        <div
          className={`tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden hover:tw-cursor-pointer`}
          style={
            isActive
              ? {
                  padding: "4px",
                  background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                }
              : {}
          }
          onClick={() => dispatchRaceTypeUpdate(type)}
        >
          <div style={raceTypeStyleObject(type)}>
            <div className={`tw-h-full tw-w-full tw-rounded-[22px] tw-relative`}>
              {raceTypeStyleObject(type).videoUrl ? (
                <>
                  <video
                    className="tw-h-full tw-w-full tw-rounded-[22px] tw-object-cover"
                    src={raceTypeStyleObject(type).videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    style={{ pointerEvents: "none" }}
                  />
                  <div className="tw-absolute tw-inset-0" />
                  <div className="tw-absolute tw-inset-0 tw-pointer-events-none" />
                  <div
                    className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-bg-indigo-900 tw-rounded-[25px] ${
                      isActive ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                    }`}
                  >
                    <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                      <span className={`hover:tw-cursor-text ${isActive ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                        <div className="tw-flex tw-flex-col tw-text-center">
                          <span className="tw-font-[700] tw-text-[36px] tw-capitalize">{type.toLowerCase()}</span>
                        </div>
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-rounded-[22px]`}>
                  <span className={`hover:tw-cursor-text ${isActive ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                    <div className="tw-flex tw-flex-col tw-text-center">
                      <span className="tw-font-[700] tw-text-[36px] tw-capitalize">{type.toLowerCase()}</span>
                    </div>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Get the upsell main track image graphql id from metaobject
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: GET_METAOBJECT_BY_HANDLE,
            variables: {
              handle: "upsell-images-react-component-main-track",
              type: "upsell_images_react_component",
            },
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const fields = data.data.metaobject.fields;
        if (fields && fields.length) {
          const mainTrackUpsellImagesIds: string[] = fields.map((item: { value: string; type: string }) => item.value);
          dispatch({
            type: "SET_MAIN_TRACK_UPSELL_IMAGE_ID_FROM_METAOBJECT",
            payload: {
              mainTrackUpsellImageIds: mainTrackUpsellImagesIds,
            },
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <>
      <ProgressBar maxValue={100} currentValue={windowWidth <= 768 ? 48 : 48} />
      {productRenderedLocation === "external" && <MiniCart />}

      {/* <span className="tw-block tw-text-[18px] lg:tw-text-[24px] tw-text-[#F609FF] tw-font-[700] tw-mt-[30px] tw-text-center">Main Track</span> */}
      {/* <div className="tw-text-white tw-text-center tw-text-[24px] tw-font-extrabold">16+ Years</div> */}

      <div className="container mx-auto">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-[30px] lg:tw-gap-[160px] tw-my-[30px] lg:tw-mt-[120px] lg:tw-mb-[40px]">
          {Object.values(RACE_TYPES).map(renderRaceTypeOption)}
        </div>
        <div className="tw-flex tw-justify-center tw-items-center tw-gap-[30px] tw-mb-[60px] md:tw-mb-0">
          {raceType === "EXPERT" && (
            <span className="tw-relative tw-flex tw-justify-center tw-items-center hover:tw-cursor-pointer" onClick={() => setIsPopupShowing((prevState) => !prevState)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="55" height="55" viewBox="0 0 58 57" fill="none">
                <path
                  d="M57.4485 28.5001C57.4485 44.215 44.7122 56.9485 29.0001 56.9485C13.2881 56.9485 0.551758 44.215 0.551758 28.5001C0.551758 12.7909 13.2881 0.0517578 29.0001 0.0517578C44.7122 0.0517578 57.4485 12.7909 57.4485 28.5001Z"
                  fill="#F609FF"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M30.9567 27.0478C30.9567 25.9256 30.0729 25.0105 28.9891 25.0105C27.9052 25.0105 27.0215 25.9256 27.0215 27.0478V37.3392C27.0215 38.4614 27.9052 39.3765 28.9891 39.3765C30.0729 39.3765 30.9567 38.4614 30.9567 37.3392V27.0478ZM27.0326 19.6603C27.0326 20.7826 27.9118 21.6976 28.9889 21.6976C30.0975 21.6976 30.979 20.7826 30.979 19.6603C30.979 18.5381 30.0975 17.623 29.0114 17.623C27.9208 17.623 27.0326 18.5381 27.0326 19.6603Z"
                  fill="white"
                />
              </svg>
            </span>
          )}
          {isPopupShowing && <ExportInfomationPopup isPopupShowing={isPopupShowing} handleClosePopup={() => setIsPopupShowing((prevState) => !prevState)} />}
        </div>
      </div>

      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px]">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div
            className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"} tw-relative tw-flex tw-justify-end tw-items-center lg:tw-block`}
          >
          </div>
        </div>
      </div>
      <BottomSummary
        previousInfo={{ pageName: "Age Group", pagePath: "/" + ROUTES.MAIN_TRACK.AGE_GROUP }}
        nextInfo={{ pageName: "Continue", pagePath: "/" + ROUTES.MAIN_TRACK.SEATS_RACES, isActive: raceType !== null }}
      />
    </>
  );
};

export default SelectRaceTypes;
